import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Download, Trash2 } from 'lucide-react';
import { aiCandidateAPI, errMsg } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';

const SCOPE_TITLES = { recording: 'Recording', ai_evaluation: 'AI evaluation', proctoring: 'Integrity checks', biometric: 'Biometric ID check' };

function downloadSummary(ctx) {
  const lines = [
    'CHIRE AI interview: consent & data summary', '',
    `Role: ${ctx.job_title || ''}${ctx.company_name ? ` at ${ctx.company_name}` : ''}`,
    `Consent version: ${ctx.consent?.version || ''}`, '',
    'What you agreed to:',
    ...Object.entries(ctx.consent?.text || {}).map(([k, v]) => `- ${SCOPE_TITLES[k] || k}: ${v}`), '',
    'Never processed: emotion or facial-expression inference, personality inference, gaze as a violation, protected attributes.', '',
    'Your rights: request accommodations or a human interview, explain any flagged moment, and delete your interview data from your invite link.',
    'AI scores and integrity flags are advisory. People at the hiring company make every decision.', '',
    `Downloaded: ${new Date().toLocaleString()}`,
  ];
  const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'chire-interview-consent-summary.txt';
  a.click();
  URL.revokeObjectURL(url);
}

const DeleteDialog = ({ open, onOpenChange, token, onDeleted }) => {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await aiCandidateAPI.deleteData(token);
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e, 'Could not delete your data'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv" data-testid="candidate-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete your interview data?</DialogTitle>
          <DialogDescription>This permanently removes your recording, transcript, report and ID images. The hiring team will see that the interview data was deleted. Type DELETE to confirm.</DialogDescription>
        </DialogHeader>
        <input className="iv-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="candidate-delete-confirm-input" />
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => onOpenChange(false)}>Cancel</button>
          <button type="button" className="iv-btn iv-btn-danger" disabled={confirm !== 'DELETE' || busy} onClick={go} data-testid="candidate-delete-confirm-btn">
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Explanation = ({ token }) => {
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const send = async () => {
    try {
      await aiCandidateAPI.explanation(token, text);
      setSent(true);
    } catch (e) {
      toast.error(errMsg(e, 'Could not send your message'));
    }
  };
  if (sent) return <div className="iv-note is-good" data-testid="candidate-explanation-sent">Thanks. Your message was shared with the reviewer.</div>;
  return (
    <section className="iv-card" data-testid="candidate-explanation-card">
      <h2 className="iv-h2">Share context about your interview</h2>
      <p className="iv-small">Some moments were flagged for a routine human review (for example a connection or camera issue). Nothing has been decided. Anything you share is added to the review.</p>
      <textarea className="iv-textarea" rows={4} value={text} onChange={(e) => setText(e.target.value)} style={{ marginTop: '0.6rem' }} data-testid="candidate-explanation-input" />
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-primary" disabled={!text.trim()} onClick={send} data-testid="candidate-explanation-submit-btn">Send</button>
      </div>
    </section>
  );
};

export function ThankYouScreen({ ctx, token, explain }) {
  const [delOpen, setDelOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const processing = ['in_progress', 'scoring', 'starting'].includes(ctx?.status);
  if (deleted) {
    return <MessageScreen title="Your interview data was deleted" body="Your recording, transcript, report and ID images have been removed. You can close this page." testId="candidate-deleted-screen" />;
  }
  return (
    <>
      <section className="iv-card" data-testid="candidate-thank-you">
        <CheckCircle2 size={36} color="#10B981" />
        <h1 className="iv-h1" style={{ marginTop: '0.6rem' }}>Thank you{ctx?.first_name ? `, ${ctx.first_name}` : ''}. Your interview is complete.</h1>
        <p className="iv-sub">Your answers have been saved. You can close this page.</p>
        <h2 className="iv-h2" style={{ marginTop: '1.25rem' }}>What happens next</h2>
        <ol className="iv-sub" style={{ paddingLeft: '1.1rem', display: 'grid', gap: '0.35rem' }}>
          <li>An AI model prepares an advisory, evidence-linked summary from the transcript.</li>
          <li>A person on the hiring team reviews your recording, transcript and the summary.</li>
          <li>The {ctx?.company_name || 'hiring'} team contacts you about next steps. No decision is made automatically.</li>
        </ol>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => downloadSummary(ctx)} data-testid="candidate-download-summary-btn"><Download size={16} /> Download consent & data summary</button>
          <button type="button" className="iv-btn iv-btn-danger" disabled={processing} onClick={() => setDelOpen(true)} title={processing ? 'Available once processing finishes' : undefined} data-testid="candidate-delete-data-btn">
            <Trash2 size={16} /> Delete my interview data
          </button>
        </div>
      </section>
      {explain && <Explanation token={token} />}
      <DeleteDialog open={delOpen} onOpenChange={setDelOpen} token={token} onDeleted={() => { setDelOpen(false); setDeleted(true); }} />
    </>
  );
}

export const MessageScreen = ({ title, body, testId, tone, children }) => (
  <section className="iv-card" data-testid={testId}>
    <h1 className="iv-h1">{title}</h1>
    <p className="iv-sub">{body}</p>
    {tone && <div className={`iv-note ${tone}`} style={{ marginTop: '0.9rem' }}>{children}</div>}
    {!tone && children}
  </section>
);
